import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { token } from "../theme/tokens.js";
import { GalleryCard } from "./GalleryCard.js";
import { GalleryEmptyState } from "./GalleryEmptyState.js";
import { GalleryLoadingSkeleton } from "./GalleryLoadingSkeleton.js";
import { GalleryPagination } from "./GalleryPagination.js";
const DEFAULT_CARD_WIDTH = 300;
const DEFAULT_SKELETON_COUNT = 6;
const GRID_GAP = 20;
export function Gallery({ tabs, activeTab, onTabChange, items, onItemClick, renderCardContent, renderCardActions, renderCardBadge, renderCardStatusActions, onCardStatusDismiss, getCardStatusRetry, cardWidth = DEFAULT_CARD_WIDTH, onContentWidthChange, loading = false, skeletonCount = DEFAULT_SKELETON_COUNT, headerRight, pagination, onItemVisible, hasMore = false, loadingMore = false, onLoadMore, }) {
    const currentTab = tabs.find((t) => t.id === activeTab);
    // Measure outer container and compute the ideal inner width
    // so tabs + grid share the same centered width.
    const outerRef = useRef(null);
    const [innerMaxWidth, setInnerMaxWidth] = useState();
    const sentinelRef = useRef(null);
    useEffect(() => {
        const el = outerRef.current;
        if (!el || typeof ResizeObserver === "undefined")
            return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            const available = Array.isArray(entry.contentBoxSize)
                ? entry.contentBoxSize[0].inlineSize
                : entry.contentRect.width;
            const cols = Math.max(1, Math.floor((available + GRID_GAP) / (cardWidth + GRID_GAP)));
            const computed = cols === 1 ? available : cols * cardWidth + (cols - 1) * GRID_GAP;
            setInnerMaxWidth(computed);
            onContentWidthChange?.(computed);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [cardWidth, onContentWidthChange]);
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel ||
            !hasMore ||
            loadingMore ||
            !onLoadMore ||
            typeof IntersectionObserver === "undefined") {
            return;
        }
        let armed = true;
        const observer = new IntersectionObserver((entries) => {
            if (armed && entries.some((entry) => entry.isIntersecting)) {
                armed = false;
                observer.disconnect();
                onLoadMore();
            }
        }, { root: findScrollParent(sentinel), rootMargin: "240px 0px" });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, onLoadMore]);
    const outerStyle = {
        width: "100%",
        minWidth: 0,
    };
    const innerStyle = {
        display: "flex",
        flexDirection: "column",
        maxWidth: innerMaxWidth,
        margin: "0 auto",
    };
    const tabBarStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "8px",
        padding: "0 0 16px 0",
        borderBottom: `1px solid ${token("cardBorder")}`,
        marginBottom: "20px",
    };
    const tabGroupStyle = {
        display: "flex",
        gap: "4px",
        maxWidth: "100%",
        overflowX: "auto",
    };
    const gridStyle = {
        display: "grid",
        gridTemplateColumns: innerMaxWidth !== undefined && innerMaxWidth < 2 * cardWidth + GRID_GAP
            ? "minmax(0, 1fr)"
            : `repeat(auto-fit, ${cardWidth}px)`,
        justifyContent: "center",
        gap: token("gridGap"),
    };
    return (_jsx("div", { ref: outerRef, style: outerStyle, children: _jsxs("div", { style: innerStyle, children: [tabs.length > 0 && (_jsxs("div", { style: tabBarStyle, children: [_jsx("div", { style: tabGroupStyle, role: "tablist", "aria-label": "Gallery tabs", children: tabs.map((tab) => (_jsx(TabButton, { tab: tab, active: tab.id === activeTab, onClick: () => onTabChange(tab.id) }, tab.id))) }), headerRight] })), _jsx("div", { style: gridStyle, role: "tabpanel", "aria-label": currentTab?.label, children: loading ? (_jsx(GalleryLoadingSkeleton, { count: skeletonCount })) : items.length === 0 && currentTab?.emptyState ? (_jsx(GalleryEmptyState, { icon: currentTab.emptyState.icon, title: currentTab.emptyState.title, description: currentTab.emptyState.description, action: currentTab.emptyState.action })) : (items.map((item) => (_jsx(GalleryCard, { item: item, onClick: onItemClick && !item.cardStatus ? () => onItemClick(item) : undefined, renderContent: renderCardContent, renderActions: renderCardActions, renderBadge: renderCardBadge, renderStatusActions: renderCardStatusActions, onDismissStatus: onCardStatusDismiss, onRetryStatus: getCardStatusRetry?.(item), onVisible: onItemVisible ? () => onItemVisible(item) : undefined }, item.id)))) }), (hasMore || loadingMore) && (_jsx("div", { ref: sentinelRef, "aria-label": loadingMore ? "Loading more drawings" : "Load more drawings", style: { minHeight: 24, padding: "12px 0", textAlign: "center" }, children: loadingMore ? "Loading more..." : "" })), pagination && !loading && items.length > 0 && (_jsx(GalleryPagination, { page: pagination.page, pageSize: pagination.pageSize, total: pagination.total, onPageChange: pagination.onPageChange }))] }) }));
}
function findScrollParent(element) {
    let parent = element.parentElement;
    while (parent) {
        const overflowY = getComputedStyle(parent).overflowY;
        if (overflowY === "auto" || overflowY === "scroll")
            return parent;
        parent = parent.parentElement;
    }
    return null;
}
/* ── Tab button (internal) ──────────────────────────────────────── */
function TabButton({ tab, active, onClick, }) {
    const style = {
        whiteSpace: "nowrap",
        flexShrink: 0,
        padding: "8px 16px",
        fontSize: "14px",
        fontWeight: active ? 600 : 400,
        border: "none",
        borderRadius: "6px",
        background: active ? token("tabActiveBg") : "transparent",
        color: active ? token("tabActiveText") : token("tabInactiveText"),
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        transition: "background 0.15s ease, color 0.15s ease",
    };
    const badgeStyle = {
        fontSize: "12px",
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: "10px",
        background: active ? "rgba(255,255,255,0.25)" : token("tabBadgeBg"),
        color: active ? token("tabActiveText") : token("tabBadgeText"),
    };
    return (_jsxs("button", { style: style, role: "tab", "aria-selected": active, onClick: onClick, children: [tab.label, tab.count != null && _jsx("span", { style: badgeStyle, children: tab.count })] }));
}
//# sourceMappingURL=Gallery.js.map